"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";

/** Only warm two relevant destinations, after the hero and while reading. */
export function CcReadingPrefetch({ hrefs, bodySelector, heroSelector }: { hrefs: string[]; bodySelector: string; heroSelector: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const key = JSON.stringify(hrefs);
  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (connection?.saveData || /^(slow-)?2g$/.test(connection?.effectiveType ?? "")) return;
    const body = document.querySelector(bodySelector);
    const hero = document.querySelector<HTMLImageElement>(heroSelector);
    if (!body) return;
    let reading = false;
    let warmed = false;
    let idle: number | undefined;
    let frame: number | undefined;
    const warm = () => {
      if (warmed || !reading || (hero && !hero.complete)) return;
      warmed = true;
      const run = () => {
        // Installed Next 16 defaults router.prefetch to AUTO (only the loading shell).
        // FULL matches Link prefetch=true. No recursive warming until navigation.
        for (const href of [...new Set(JSON.parse(key) as string[])].filter(href => href.startsWith("/") && !href.startsWith("//") && href !== pathname).slice(0, 2)) {
          router.prefetch(href, { kind: PrefetchKind.FULL });
        }
      };
      if (window.requestIdleCallback) idle = window.requestIdleCallback(run, { timeout: 1000 });
      else frame = requestAnimationFrame(run);
    };
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { reading = true; warm(); }
    });
    observer.observe(body);
    hero?.addEventListener("load", warm);
    hero?.addEventListener("error", warm);
    return () => {
      observer.disconnect();
      hero?.removeEventListener("load", warm);
      hero?.removeEventListener("error", warm);
      if (idle !== undefined) window.cancelIdleCallback(idle);
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [key, pathname, router, bodySelector, heroSelector]);
  return null;
}
